import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, Search } from "@mui/icons-material";

const normalizeYearName = (name) => String(name || "").trim().toLowerCase().replace(/\s+/g, "");

// Ids may be numeric (question bank) or canonical tokens like "t:628" (student worksheets).
const idKey = (id) => (id == null ? null : String(id));

const yearNumberOf = (name) => {
  const match = String(name ?? "").match(/(\d+)/);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
};

const TopicTreeSelect = ({
  value = [],
  onChange,
  catalog = [],
  yearId = "",
  onYearChange,
  schoolYears = [],
  disabled = false,
  label = "Topics",
  defaultExpanded = true,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedParents, setExpandedParents] = useState({});
  const [searchText, setSearchText] = useState("");

  const selectedIds = useMemo(
    () => new Set((value || []).map(idKey)),
    [value]
  );

  const yearFilteredCatalog = useMemo(() => {
    if (!yearId) return catalog;
    // A canonical topic can belong to several years at once, hence school_year_ids.
    return catalog.filter((t) => (
      Array.isArray(t.school_year_ids)
        ? t.school_year_ids.some((id) => Number(id) === Number(yearId))
        : Number(t.school_year_id || 0) === Number(yearId)
    ));
  }, [catalog, yearId]);

  // Nests to whatever depth the catalog declares: the question bank sends topic -> subtopic,
  // the student worksheet filter sends subject -> topic -> subtopic.
  const tree = useMemo(() => {
    const nodes = new Map();
    yearFilteredCatalog.forEach((t) => nodes.set(idKey(t.id), { ...t, children: [] }));

    const roots = [];
    nodes.forEach((node) => {
      const parent = nodes.get(idKey(node.parent_id));
      if (parent && parent !== node) parent.children.push(node);
      else roots.push(node);
    });

    const sortNodes = (list) => {
      list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      list.forEach((n) => sortNodes(n.children));
    };
    sortNodes(roots);

    return roots;
  }, [yearFilteredCatalog]);

  const searchFilteredTree = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return tree;

    // A node survives if it matches, or if any descendant does. A matching node keeps all of
    // its children so a topic hit still lists everything under it.
    const prune = (node) => {
      const matched = String(node.name || "").toLowerCase().includes(term)
        || String(node.label || "").toLowerCase().includes(term);
      if (matched) return node;
      const children = node.children.map(prune).filter(Boolean);
      return children.length ? { ...node, children } : null;
    };

    return tree.map(prune).filter(Boolean);
  }, [tree, searchText]);

  const descendantIds = (node) => {
    const ids = [idKey(node.id)];
    node.children.forEach((child) => ids.push(...descendantIds(child)));
    return ids;
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? "topic-tree-select-popover" : undefined;

  const toggleParentExpanded = (parentId) => {
    setExpandedParents((prev) => ({ ...prev, [parentId]: !prev[parentId] }));
  };

  const toggleSingle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange?.(Array.from(next));
  };

  const toggleBranch = (node) => {
    const ids = descendantIds(node);
    const allSelected = ids.every((id) => selectedIds.has(id));
    const next = new Set(selectedIds);
    ids.forEach((id) => {
      if (allSelected) next.delete(id);
      else next.add(id);
    });
    onChange?.(Array.from(next));
  };

  const clearAll = () => onChange?.([]);

  const selectedCount = selectedIds.size;
  const selectedTopics = useMemo(() => {
    if (selectedIds.size === 0) return [];
    return catalog
      .filter((t) => selectedIds.has(idKey(t.id)))
      .map((t) => ({ id: idKey(t.id), label: t.label || t.name || `#${t.id}` }));
  }, [catalog, selectedIds]);

  // school_years ids are not in year order (Year 1 is id 10), so sort on the number in the name.
  const yearTabs = useMemo(() => {
    const seen = new Map();
    (schoolYears || []).forEach((y) => {
      const key = normalizeYearName(y?.name);
      if (key && !seen.has(key)) seen.set(key, y);
    });
    return [
      { id: "", name: "All" },
      ...Array.from(seen.values())
        .sort((a, b) => yearNumberOf(a.name) - yearNumberOf(b.name))
        .map((y) => ({ id: String(y.id), name: y.name })),
    ];
  }, [schoolYears]);

  const renderNode = (node, depth) => {
    const nodeId = idKey(node.id);
    const children = node.children || [];
    const isExpanded = expandedParents[nodeId] ?? (searchText.trim() ? true : defaultExpanded);
    const countText = node.question_count != null ? ` (${node.question_count})` : "";

    return (
      <Box key={nodeId}>
        <ListItemButton
          sx={{ pl: 2 + depth * 2.5, py: depth === 0 ? 0.5 : 0.25 }}
          onClick={() => (children.length ? toggleBranch(node) : toggleSingle(nodeId))}
        >
          <Checkbox
            size="small"
            checked={selectedIds.has(nodeId)}
            onChange={() => (children.length ? toggleBranch(node) : toggleSingle(nodeId))}
            onClick={(e) => e.stopPropagation()}
          />
          <ListItemText
            primary={
              <Typography variant="body2" sx={{ fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 400 }}>
                {node.name}
                <Typography component="span" variant="caption" color="text.secondary">
                  {countText}
                </Typography>
              </Typography>
            }
          />
          {children.length > 0 && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleParentExpanded(nodeId);
              }}
            >
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          )}
        </ListItemButton>
        {children.length > 0 && isExpanded && children.map((child) => renderNode(child, depth + 1))}
      </Box>
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={disabled}
        sx={{ minHeight: 40, justifyContent: "flex-start", width: "100%", textTransform: "none", py: 0.5 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", width: "100%" }}>
          {selectedTopics.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{label}</Typography>
          ) : (
            <>
              {selectedTopics.slice(0, 4).map((t) => (
                <Chip
                  key={t.id}
                  size="small"
                  label={t.label}
                  onDelete={(e) => { e.stopPropagation(); toggleSingle(t.id); }}
                  onClick={(e) => e.stopPropagation()}
                />
              ))}
              {selectedTopics.length > 4 && (
                <Chip size="small" variant="outlined" label={`+${selectedTopics.length - 4} more`} />
              )}
            </>
          )}
        </Box>
      </Button>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { width: 420, maxWidth: "95vw" } }}
      >
        <Box sx={{ p: 1.5 }}>
          {yearTabs.length > 1 && (
            <Tabs
              value={String(yearId || "")}
              onChange={(_, next) => onYearChange?.(next)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 36 }}
            >
              {yearTabs.map((y) => (
                <Tab key={String(y.id)} value={String(y.id)} label={y.name} sx={{ minHeight: 36, textTransform: "none" }} />
              ))}
            </Tabs>
          )}
          <TextField
            size="small"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search topics or subtopics"
            sx={{ mt: yearTabs.length > 1 ? 1 : 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />
        <List dense sx={{ maxHeight: 420, overflowY: "auto", py: 0 }}>
          {searchFilteredTree.map((node) => renderNode(node, 0))}
          {searchFilteredTree.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No topics match your search.
              </Typography>
            </Box>
          )}
        </List>
        <Divider />
        <Box sx={{ p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button size="small" onClick={clearAll} sx={{ textTransform: "none" }}>
            Clear all
          </Button>
          <Button size="small" variant="contained" onClick={() => setAnchorEl(null)} sx={{ textTransform: "none" }}>
            Done
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default TopicTreeSelect;
